<script setup lang="ts">
import { ref, nextTick } from 'vue'
import type { NodeProps } from '@vue-flow/core'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import Popover from 'primevue/popover'
import VueFlowMiniPreview from './VueFlowMiniPreview.vue'

library.add(faCircleInfo)

const props = defineProps<NodeProps>()
const op = ref()

function repositionToLeft(iconEl: HTMLElement) {
	// PrimeVue Popover uses a container ref internally
	const panel = op.value?.container
	if (!panel) return
	const iconRect = iconEl.getBoundingClientRect()
	const popoverWidth = panel.offsetWidth || 300
	panel.style.left = (iconRect.left - popoverWidth - 10) + 'px'
	panel.style.top = (iconRect.top - 10) + 'px'
}

async function toggle(event: Event) {
	const iconEl = event.target as HTMLElement
	op.value.toggle(event)
	await nextTick()
	requestAnimationFrame(() => repositionToLeft(iconEl))
}
</script>

<template>
	<div class="template-group-node" :style="{ width: data.width + 'px', height: data.height + 'px', backgroundColor: data.groupColor || undefined }">
		<div class="template-group-label">
			<FontAwesomeIcon
				:icon="['far', 'circle-info']"
				class="cursor-pointer text-blue-500 hover:text-blue-700"
				@click="toggle"
			/>
		</div>
		<Popover ref="op">
			<div class="p-3" style="min-width: 280px;">
				<div class="text-sm font-semibold mb-2">Copied from Template</div>
				<div class="text-xs text-gray-600 mb-1">
					<span class="font-medium">Project:</span> {{ data.templateSourceProjectName }}
				</div>
				<div class="text-xs text-gray-600 mb-1">
					<span class="font-medium">Subproject:</span> {{ data.templateSourceSubProjectName }}
				</div>
				<div class="text-xs text-gray-600 mb-1">
					<span class="font-medium">Assessment:</span> {{ data.templateSourceAssessmentName }}
				</div>
				<div v-if="data.templateScreenName" class="text-xs text-gray-600 mb-3">
					<span class="font-medium">Screen:</span> {{ data.templateScreenName }}
				</div>
				<div v-if="data.originalGraph && data.originalGraph.steps.length > 0">
					<div class="text-xs font-medium mb-1">Original Step Sequence:</div>
					<VueFlowMiniPreview :steps="data.originalGraph.steps" :connections="data.originalGraph.connections" />
				</div>
			</div>
		</Popover>
	</div>
</template>

<style scoped>
.template-group-node {
	border: 2px dashed var(--p-text-color-secondary, #9CA3AF);
	border-radius: 8px;
	background-color: color-mix(in srgb, var(--p-text-color-secondary, #9CA3AF) 5%, transparent);
	position: relative;
}

.template-group-label {
	position: absolute;
	top: -10px;
	left: 12px;
	background-color: white;
	padding: 0 6px;
	font-size: 14px;
	white-space: nowrap;
}
</style>
